import { lazy, Suspense, useEffect, useId, useState } from 'react';
import {
  Archive,
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  Mail,
  MoreVertical,
  Reply,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import {
  FloatingFocusManager,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/overlays/Popover';
import { trackGenerateLead } from '@/lib/subscribeFunnelAnalytics';

const CONTACT_EMAIL = 'walker@atrivial.com';
const SUBJECT = 'Make your newsletter a habit';
const NewsletterBonusQuestion = lazy(() => import('./NewsletterBonusQuestion'));
const iconButton =
  'newsletter-demo-email-icon-button text-glyph-secondary hover:bg-subtle focus-visible:outline-accent-primary inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition focus-visible:outline-2 focus-visible:outline-offset-2';
const actionLink =
  'border-outline-default hover:bg-subtle focus-visible:outline-accent-primary inline-flex min-h-11 items-center justify-center gap-2 rounded-full border px-4 text-sm font-medium focus-visible:outline-2';
const menuButton =
  'hover:bg-subtle focus-visible:outline-accent-primary min-h-11 rounded-lg px-3 text-left text-sm focus-visible:outline-2';

type Panel = 'contact' | 'reply' | 'trivia' | null;
type Folder = 'Inbox' | 'Archived' | 'Trash';

/** The email demo's mailbox controls stay independent of the live Quiz handoff. */
export default function NewsletterEmailChrome({
  sentAt,
  onChangeTheme,
  onWarmThemes,
}: {
  sentAt: Date | null;
  onChangeTheme: () => Promise<void>;
  onWarmThemes: () => void;
}) {
  const replyId = useId();
  const panelTitleId = useId();
  const [chromeRoot, setChromeRoot] = useState<HTMLDivElement | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [folder, setFolder] = useState<Folder>('Inbox');
  const [notice, setNotice] = useState('');
  const [unread, setUnread] = useState(false);
  const [openMenu, setOpenMenu] = useState<'message' | 'inbox' | null>(null);
  const [draft, setDraft] = useState('');
  const [isStarGlimmering, setIsStarGlimmering] = useState(false);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let nextGlimmer: number | undefined;
    let glimmerEnd: number | undefined;
    const clearTimers = () => {
      window.clearTimeout(nextGlimmer);
      window.clearTimeout(glimmerEnd);
    };
    const scheduleGlimmer = () => {
      nextGlimmer = window.setTimeout(
        () => {
          setIsStarGlimmering(true);
          glimmerEnd = window.setTimeout(() => setIsStarGlimmering(false), 900);
          scheduleGlimmer();
        },
        5000 + Math.random() * 10000
      );
    };
    const updateMotion = () => {
      clearTimers();
      setIsStarGlimmering(false);
      if (!reducedMotion.matches && !document.hidden) scheduleGlimmer();
    };
    updateMotion();
    reducedMotion.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateMotion);
    return () => {
      clearTimers();
      reducedMotion.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateMotion);
    };
  }, []);
  const { refs, context } = useFloating({
    open: panel !== null,
    onOpenChange: (open) => {
      if (!open) setPanel(null);
    },
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });
  const { getFloatingProps } = useInteractions([dismiss, role]);
  const panelTitle =
    panel === 'contact'
      ? 'Atrivial'
      : panel === 'reply'
        ? 'Reply to Atrivial'
        : 'A tiny trivia break';
  const fullDate = sentAt?.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const shortDate = sentAt?.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const openPanel = (next: Panel) => {
    setOpenMenu(null);
    setPanel(next);
  };
  const moveTo = (next: Folder) => {
    setFolder(next);
    setNotice(
      next === 'Inbox'
        ? 'Back in your inbox.'
        : next === 'Archived'
          ? 'Archived. A keeper for later.'
          : 'Moved to trash. Tough crowd!'
    );
    setOpenMenu(null);
  };
  const changeTheme = () => {
    void onChangeTheme().catch(() => {
      setNotice('Couldn’t change the theme. Click the star to try again.');
    });
  };
  const moreOptions = (
    <Popover
      placement="bottom-end"
      portalRoot={chromeRoot}
      open={openMenu === 'message'}
      onOpenChange={(open) => setOpenMenu(open ? 'message' : null)}
    >
      <PopoverTrigger className={iconButton} aria-label="More message options" title="More options">
        <MoreVertical size={18} aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent
        aria-label="Email options"
        className="w-56 max-w-full rounded-xl border border-outline-default bg-raised p-2 text-glyph-primary shadow-default"
      >
        <button
          type="button"
          className={`${menuButton} flex items-center gap-2`}
          onClick={() => {
            changeTheme();
            setOpenMenu(null);
          }}
        >
          <Star size={16} className="newsletter-demo-theme-star fill-current" aria-hidden="true" />
          Change theme
        </button>
        <button type="button" className={menuButton} onClick={() => openPanel('reply')}>
          Reply
        </button>
        <button type="button" className={menuButton} onClick={() => openPanel('contact')}>
          Meet Atrivial
        </button>
        <button type="button" className={menuButton} onClick={() => openPanel('trivia')}>
          A tiny trivia break
        </button>
      </PopoverContent>
    </Popover>
  );

  return (
    <div
      ref={setChromeRoot}
      onPointerEnter={onWarmThemes}
      onFocusCapture={onWarmThemes}
      onTouchStart={onWarmThemes}
      data-testid="newsletter-demo-email-chrome"
      className="newsletter-demo-email-chrome shrink-0 bg-raised"
    >
      <div
        data-testid="newsletter-demo-email-toolbar"
        className="newsletter-demo-email-toolbar flex items-center px-1 text-glyph-secondary"
      >
        <Popover
          placement="bottom-start"
          portalRoot={chromeRoot}
          open={openMenu === 'inbox'}
          onOpenChange={(open) => setOpenMenu(open ? 'inbox' : null)}
        >
          <PopoverTrigger className={iconButton} aria-label="Back to inbox" title="Back to inbox">
            <ArrowLeft size={20} aria-hidden="true" />
          </PopoverTrigger>
          <PopoverContent
            aria-label="Demo inbox"
            className="w-64 max-w-full rounded-xl border border-outline-default bg-raised p-3 text-glyph-primary shadow-default"
          >
            <p className="mb-2 text-sm font-semibold">
              {folder === 'Inbox' ? 'Inbox · 1 message' : 'Inbox zero'}
            </p>
            <button
              type="button"
              className={`${menuButton} flex flex-col justify-center gap-1 py-2`}
              onClick={() => {
                setUnread(false);
                setOpenMenu(null);
              }}
            >
              <strong>Atrivial {unread ? '· Unread' : ''}</strong>
              <span>{SUBJECT}</span>
              {folder !== 'Inbox' && (
                <span className="text-xs text-glyph-tertiary">In {folder.toLowerCase()}</span>
              )}
            </button>
            <p className="mt-2 text-xs text-glyph-tertiary">One email. Plenty to discover.</p>
          </PopoverContent>
        </Popover>
        <div className="ml-auto flex">
          <button
            type="button"
            className={iconButton}
            aria-label="Archive email"
            title="Archive"
            onClick={() => moveTo('Archived')}
          >
            <Archive size={19} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={iconButton}
            aria-label="Delete email"
            title="Delete"
            onClick={() => moveTo('Trash')}
          >
            <Trash2 size={19} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={iconButton}
            aria-label={unread ? 'Mark as read' : 'Mark as unread'}
            title={unread ? 'Mark as read' : 'Mark as unread'}
            onClick={() => {
              setUnread(!unread);
              setNotice(unread ? 'Marked as read.' : 'Marked unread. Worth a second look.');
            }}
          >
            <Mail size={19} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`${iconButton} ${isStarGlimmering ? 'newsletter-demo-theme-glimmer' : ''}`}
            aria-label="Change theme"
            title="Change theme"
            onClick={changeTheme}
          >
            <span className="newsletter-demo-theme-icon" aria-hidden="true">
              <Star size={19} className="newsletter-demo-theme-star fill-current" />
              <Star size={19} className="newsletter-demo-theme-glint fill-current" />
            </span>
          </button>
        </div>
      </div>

      <div className="newsletter-demo-email-details border-b border-outline-default px-4 pb-3">
        <p
          data-testid="newsletter-demo-email-subject"
          className="newsletter-demo-email-subject m-0 min-w-0 flex-1 leading-snug font-normal whitespace-nowrap text-glyph-primary"
        >
          {SUBJECT}{' '}
          <button
            type="button"
            className="newsletter-demo-email-folder rounded bg-subtle align-middle text-glyph-secondary"
            onClick={() => setOpenMenu('inbox')}
          >
            {folder}
          </button>
        </p>
        <div className="newsletter-demo-email-sender-row flex items-start">
          <button
            type="button"
            data-testid="newsletter-demo-email-avatar"
            aria-label="View Atrivial contact"
            title="Meet Atrivial"
            onClick={() => openPanel('contact')}
            className="newsletter-demo-email-avatar flex shrink-0 items-center justify-center rounded-full bg-inverted font-medium text-glyph-inverted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-primary"
          >
            A
          </button>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5 self-center">
            <div className="newsletter-demo-email-sender flex items-baseline whitespace-nowrap">
              <button
                type="button"
                className="newsletter-demo-email-sender-name leading-tight font-semibold hover:underline focus-visible:outline-2 focus-visible:outline-accent-primary"
                onClick={() => openPanel('contact')}
              >
                Atrivial
              </button>
              <time
                data-testid="newsletter-demo-email-time"
                dateTime={sentAt?.toISOString()}
                title={fullDate}
                className="newsletter-demo-email-time leading-tight whitespace-nowrap text-glyph-tertiary"
              >
                {shortDate ?? '\u00a0'}
              </time>
            </div>
            <details className="newsletter-demo-sender">
              <summary
                aria-label="Message details"
                className="flex cursor-pointer items-center gap-0.5 leading-tight text-glyph-secondary focus-visible:outline-2 focus-visible:outline-accent-primary"
              >
                to you
                <ChevronDown
                  size={12}
                  className="newsletter-demo-sender-chevron"
                  aria-hidden="true"
                />
              </summary>
              <dl
                data-testid="newsletter-demo-email-metadata"
                className="newsletter-demo-email-metadata mt-2 grid gap-x-2 gap-y-1.5 rounded-lg border border-outline-default bg-subtle p-2 text-[10px] leading-relaxed"
              >
                <dt>From</dt>
                <dd className="min-w-0 break-words">
                  <a href={`mailto:${CONTACT_EMAIL}`} className="underline">
                    {CONTACT_EMAIL}
                  </a>
                </dd>
                <dt>To</dt>
                <dd className="min-w-0 break-words">You &lt;reader@example.com&gt;</dd>
                <dt>Date</dt>
                <dd>{fullDate}</dd>
              </dl>
            </details>
          </div>
          <button
            type="button"
            className={iconButton}
            aria-label="Reply to Atrivial"
            title="Reply"
            onClick={() => openPanel('reply')}
          >
            <Reply size={19} aria-hidden="true" />
          </button>
          {moreOptions}
        </div>
      </div>

      {notice && (
        <div className="absolute right-3 bottom-3 left-3 z-30 flex items-center gap-2 rounded-xl bg-inverted px-3 py-2 text-xs text-glyph-inverted shadow-default">
          <span role="status" className="flex-1">
            {notice}
          </span>
          {folder !== 'Inbox' && (
            <button
              type="button"
              className="min-h-11 font-semibold underline"
              onClick={() => moveTo('Inbox')}
            >
              Undo
            </button>
          )}
          <button
            type="button"
            aria-label="Dismiss notification"
            className="flex h-11 w-11 shrink-0 items-center justify-center"
            onClick={() => setNotice('')}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      )}

      {panel !== null && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-overlay/40 p-3">
          <FloatingFocusManager context={context}>
            <div
              ref={refs.setFloating}
              {...getFloatingProps()}
              aria-labelledby={panelTitleId}
              className="flex max-h-full w-full min-w-0 flex-col rounded-2xl border border-outline-default bg-raised text-glyph-primary shadow-default outline-none"
            >
              <header className="flex shrink-0 items-center justify-between gap-2 border-b border-outline-default px-4 py-2">
                <h2 id={panelTitleId} className="m-0 text-base font-semibold">
                  {panelTitle}
                </h2>
                <button
                  type="button"
                  className={iconButton}
                  aria-label="Close"
                  onClick={() => setPanel(null)}
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </header>
              <div className="min-h-0 overflow-y-auto overscroll-contain p-4">
                {panel === 'contact' && (
                  <div className="flex flex-col items-center gap-4 py-2 text-center">
                    <span
                      className="flex h-16 w-16 items-center justify-center rounded-full bg-inverted text-2xl text-glyph-inverted"
                      aria-hidden="true"
                    >
                      A
                    </span>
                    <div>
                      <p className="text-lg font-semibold">Hi, we’re Atrivial.</p>
                      <p className="mt-1 text-sm text-glyph-secondary">
                        The news quiz automation tool for newsletters and digital publishers
                      </p>
                    </div>
                    <a className={actionLink} href={`mailto:${CONTACT_EMAIL}`}>
                      <Mail size={16} aria-hidden="true" />
                      {CONTACT_EMAIL}
                    </a>
                    <a
                      className={actionLink}
                      href="https://calendly.com/walker-atrivial/30min"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackGenerateLead('schedule_demo')}
                    >
                      <CalendarDays size={16} aria-hidden="true" />
                      Schedule call
                    </a>
                  </div>
                )}
                {panel === 'reply' && (
                  <div className="flex flex-col gap-3">
                    <p className="text-sm text-glyph-secondary">To: {CONTACT_EMAIL}</p>
                    <label htmlFor={replyId} className="text-sm font-medium">
                      Your message
                    </label>
                    <textarea
                      id={replyId}
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      rows={4}
                      placeholder="Hey Atrivial, tell me more…"
                      className="w-full resize-y rounded-lg border border-outline-default bg-subtle p-3 text-sm focus-visible:outline-2 focus-visible:outline-accent-primary"
                    />
                    <a
                      className={actionLink}
                      href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Re: ${SUBJECT}`)}&body=${encodeURIComponent(draft)}`}
                    >
                      Continue in email <Reply size={16} aria-hidden="true" />
                    </a>
                  </div>
                )}
                {panel === 'trivia' && (
                  <Suspense
                    fallback={
                      <div role="status" aria-label="Loading bonus question">
                        <div className="skeleton-shimmer mb-4 h-12 rounded-md" aria-hidden="true" />
                        <div className="skeleton-shimmer h-48 rounded-lg" aria-hidden="true" />
                      </div>
                    }
                  >
                    <NewsletterBonusQuestion />
                  </Suspense>
                )}
              </div>
            </div>
          </FloatingFocusManager>
        </div>
      )}
    </div>
  );
}
